/* EB price filter — drives the dual-handle slider and keeps it in sync with the
   two text inputs that Dawn's facet form actually serialises.

   The wiring into Dawn is deliberately indirect: the slider never talks to
   FacetFiltersForm. It writes the value into the `filter.v.price.gte`/`.lte`
   text inputs, and the range input's own `input` event then bubbles on to the
   form, where Dawn's 800ms-debounced listener re-renders the grid. Because our
   handler sits on the range input (target phase) it always runs before the
   form's (bubble phase), so the fields are up to date by the time Dawn reads
   them with `new FormData(form)`.

   Values are currency units, never cents — the snippet emits plain numbers so
   there is no locale-formatted text to parse. A field is left EMPTY whenever the
   handle sits on an end of the range, so an unconstrained side never adds a
   redundant param (and never draws an "0 – max" active-filter pill). */

if (!customElements.get('eb-price-range')) {
  customElements.define(
    'eb-price-range',
    class EbPriceRange extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;

        this.slider = this.querySelector('[data-eb-pf-slider]');
        this.rail = this.querySelector('.eb-price-facet__rail');
        this.minThumb = this.querySelector('[data-eb-pf-thumb="min"]');
        this.maxThumb = this.querySelector('[data-eb-pf-thumb="max"]');
        this.minField = this.querySelector('[data-eb-pf-field="min"]');
        this.maxField = this.querySelector('[data-eb-pf-field="max"]');

        if (!this.slider || !this.minThumb || !this.maxThumb || !this.minField || !this.maxField) return;
        this.ready = true;

        this.rangeMax = this.toNumber(this.dataset.ebPfMax);
        if (!Number.isFinite(this.rangeMax) || this.rangeMax <= 0) this.rangeMax = 0;

        this.step = this.niceStep(this.rangeMax);
        this.sliderMax = this.rangeMax > 0 ? this.round(Math.ceil(this.rangeMax / this.step) * this.step) : 1;

        this.min = this.toNumber(this.dataset.ebPfMinValue);
        this.max = this.toNumber(this.dataset.ebPfMaxValue);

        this.onThumbInput = this.handleThumbInput.bind(this);
        this.onFieldInput = this.handleFieldInput.bind(this);
        this.onFieldChange = this.handleFieldChange.bind(this);
        this.onFieldKeyDown = this.handleFieldKeyDown.bind(this);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);

        [this.minThumb, this.maxThumb].forEach((thumb) => {
          thumb.min = '0';
          thumb.max = String(this.sliderMax);
          thumb.step = String(this.step);
          thumb.addEventListener('input', this.onThumbInput);
        });

        [this.minField, this.maxField].forEach((field) => {
          field.addEventListener('input', this.onFieldInput);
          field.addEventListener('change', this.onFieldChange);
          field.addEventListener('keydown', this.onFieldKeyDown);
        });

        this.slider.addEventListener('pointerdown', this.onPointerDown);
        this.slider.addEventListener('pointermove', this.onPointerMove);
        this.slider.addEventListener('pointerup', this.onPointerUp);
        this.slider.addEventListener('pointercancel', this.onPointerUp);

        this.render();
      }

      /* State ------------------------------------------------------------- */

      /* `this.min` / `this.max` are NaN while that side is unconstrained; this
         resolves them to real positions and guarantees min <= max. */
      get values() {
        const min = Number.isFinite(this.min) ? this.clamp(this.min, 0, this.rangeMax) : 0;
        let max = Number.isFinite(this.max) ? this.clamp(this.max, 0, this.rangeMax) : this.rangeMax;
        if (max < min) max = min;
        return { min, max };
      }

      render(skipField) {
        const { min, max } = this.values;
        const span = this.sliderMax || 1;

        this.minThumb.value = String(min);
        this.maxThumb.value = String(max);

        this.slider.style.setProperty('--eb-pf-from', `${(min / span) * 100}%`);
        this.slider.style.setProperty('--eb-pf-to', `${(max / span) * 100}%`);

        if (skipField !== this.minField) this.minField.value = min > 0 ? this.format(min) : '';
        if (skipField !== this.maxField) this.maxField.value = max < this.rangeMax ? this.format(max) : '';

        this.stack();
      }

      /* Only the knobs take pointer events, so the two inputs never fight — but
         when the values sit within a knob of each other the knobs do overlap.
         Raise whichever one the pointer is heading for; with no pointer, keep
         the max knob on top unless both are pinned to the end of the range (in
         which case only the min knob has anywhere left to go). */
      stack(pointerValue) {
        const { min, max } = this.values;
        let minOnTop;

        if (Number.isFinite(pointerValue)) {
          const toMin = Math.abs(pointerValue - min);
          const toMax = Math.abs(pointerValue - max);
          minOnTop = toMin < toMax || (toMin === toMax && min >= this.rangeMax);
        } else {
          minOnTop = min >= max && min >= this.rangeMax;
        }

        // Hover re-evaluates this on every pointermove — only touch the DOM on a flip.
        if (minOnTop === this.minOnTop) return;
        this.minOnTop = minOnTop;
        this.minThumb.style.zIndex = minOnTop ? '4' : '3';
        this.maxThumb.style.zIndex = minOnTop ? '3' : '4';
      }

      /* Events ------------------------------------------------------------ */

      handleThumbInput(event) {
        const thumb = event.currentTarget;
        const value = this.toNumber(thumb.value);
        if (!Number.isFinite(value)) return;

        const current = this.values;
        const next = this.clamp(value, 0, this.rangeMax);

        if (thumb === this.minThumb) {
          this.min = Math.min(next, current.max);
        } else {
          this.max = Math.max(next, current.min);
        }

        this.render();
      }

      handleFieldInput(event) {
        const field = event.currentTarget;
        const value = this.toNumber(field.value);

        if (field === this.minField) {
          this.min = value;
        } else {
          this.max = value;
        }

        // Skip the field being typed in — reformatting mid-keystroke fights the shopper.
        this.render(field);
      }

      /* On blur/enter the value is normalised (clamped, min/max ordered). If
         that actually changed the text, re-fire `input` so Dawn re-queries with
         the corrected value rather than the one the shopper typed. */
      handleFieldChange() {
        const before = [this.minField.value, this.maxField.value];
        this.render();

        [this.minField, this.maxField].forEach((field, index) => {
          if (field.value !== before[index]) field.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }

      handleFieldKeyDown(event) {
        if (event.metaKey || event.ctrlKey) return;

        const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
        if (!event.key.match(pattern)) event.preventDefault();
      }

      handlePointerMove(event) {
        if (this.dragging) {
          this.moveTo(this.dragging, this.valueFromPointer(event));
          return;
        }

        // Pre-raise the knob the pointer is heading for, before it can be pressed.
        if (event.buttons !== 0) return;
        this.stack(this.valueFromPointer(event));
      }

      /* A press on the bare rail moves the nearer knob there and keeps dragging
         it. A press on a knob is left to the browser's own drag handling — which
         also means that if a browser ever refuses pointer events on the range
         thumb, every press simply falls through to this drag instead. */
      handlePointerDown(event) {
        if (event.button !== 0 || this.rangeMax <= 0) return;
        if (event.target === this.minThumb || event.target === this.maxThumb) {
          this.stack(this.valueFromPointer(event));
          return;
        }

        const value = this.valueFromPointer(event);
        if (!Number.isFinite(value)) return;

        const { min, max } = this.values;
        const toMin = Math.abs(value - min);
        const toMax = Math.abs(value - max);
        const thumb = toMin < toMax || (toMin === toMax && value < max) ? this.minThumb : this.maxThumb;

        this.dragging = thumb;
        if (this.slider.setPointerCapture) this.slider.setPointerCapture(event.pointerId);
        thumb.focus();
        this.moveTo(thumb, value);
      }

      handlePointerUp(event) {
        if (!this.dragging) return;
        this.dragging = null;
        if (this.slider.releasePointerCapture && this.slider.hasPointerCapture(event.pointerId)) {
          this.slider.releasePointerCapture(event.pointerId);
        }
      }

      /* Moves one knob and lets the change out to Dawn the same way a native
         drag would — by firing `input` from the range input itself. */
      moveTo(thumb, value) {
        if (!Number.isFinite(value)) return;

        const { min, max } = this.values;
        if (thumb === this.minThumb) {
          if (Math.min(value, max) === min) return;
          this.min = Math.min(value, max);
        } else {
          if (Math.max(value, min) === max) return;
          this.max = Math.max(value, min);
        }

        this.render();
        thumb.dispatchEvent(new Event('input', { bubbles: true }));
      }

      /* Helpers ----------------------------------------------------------- */

      /* The rail is already inset by half a knob on each side, so its box is
         exactly the range the knob centres travel across. */
      valueFromPointer(event) {
        if (!this.rail) return NaN;
        const rect = this.rail.getBoundingClientRect();
        if (!rect.width) return NaN;

        const ratio = this.clamp((event.clientX - rect.left) / rect.width, 0, 1);
        return this.round(this.snap(ratio * this.sliderMax));
      }

      snap(value) {
        if (!(this.step > 0)) return value;
        return Math.round(value / this.step) * this.step;
      }

      /* ~1% of the range, rounded to a 1/2/5 figure: fine enough to drag
         smoothly, coarse enough that arrow keys cross the range in ~100 presses. */
      niceStep(range) {
        if (!(range > 0)) return 1;

        const raw = range / 100;
        const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
        const normalised = raw / magnitude;
        const multiplier = normalised >= 5 ? 5 : normalised >= 2 ? 2 : 1;

        return Math.max(this.round(magnitude * multiplier), 0.01);
      }

      toNumber(value) {
        if (value === null || value === undefined) return NaN;

        const text = String(value)
          .replace(/[\s,']/g, '')
          .trim();
        if (text === '') return NaN;

        const number = Number(text);
        return Number.isFinite(number) ? number : NaN;
      }

      format(value) {
        const rounded = this.round(value);
        return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
      }

      round(value) {
        return Math.round(value * 100) / 100;
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }
    }
  );
}
